/**
 * Shared size constants for canvas frames, nodes, and layout.
 *
 * Single source of truth — all frame/node creation, drop sizing,
 * and collision avoidance should import from here.
 */

// ── Frame defaults ──────────────────────────────────────────────────

/** Default 9:16 frame dimensions (used when creating a new frame). */
export const FRAME_W = 512;
export const FRAME_H = 910;

/** Gap between a source node and a newly created frame. */
export const FRAME_GAP = 40;

/** Padding from frame edges when placing children inside a frame. */
export const FRAME_PADDING = 40;

/** Pixel sizes for each supported frame aspect ratio. */
export const RATIO_SIZES: Record<string, { width: number; height: number }> = {
  "9:16": { width: 282, height: 500 },
  "1:1": { width: 500, height: 500 },
  "16:9": { width: 889, height: 500 },
  "2:3": { width: 333, height: 500 },
  "3:2": { width: 750, height: 500 },
};

/**
 * "Let the model pick the shape" — sent verbatim as `aspect_ratio`; the BE drops the
 * field from the image-model payload for this value. Generation-only: a frame has a
 * fixed shape, so it is never a key of RATIO_SIZES and never offered in frame mode.
 */
export const AUTO_ASPECT_RATIO = "auto";

/** Aspect ratios offered by the generation panel (frame mode omits AUTO_ASPECT_RATIO). */
export const GENERATION_RATIOS = [AUTO_ASPECT_RATIO, "9:16", "1:1", "16:9", "2:3", "3:2"];

// ── Node drop / upload limits ───────────────────────────────────────

/** Maximum display dimensions when dropping or uploading an image. */
export const NODE_MAX_W = 600;
export const NODE_MAX_H = 600;

/**
 * Scale image dimensions to fit within NODE_MAX_W × NODE_MAX_H,
 * preserving aspect ratio. Returns the original size if already within bounds.
 */
export const fitNodeBounds = (imgW: number, imgH: number): { width: number; height: number } => {
  let width = imgW;
  let height = imgH;
  const ratio = width / height;

  if (width > NODE_MAX_W) {
    width = NODE_MAX_W;
    height = width / ratio;
  }
  if (height > NODE_MAX_H) {
    height = NODE_MAX_H;
    width = height * ratio;
  }

  return { width, height };
};
